import * as React from 'react';
import { cn } from '@/lib/utils';

/* ─── Card ─────────────────────────────────────────────────────────────────── */

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  hoverable?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, hoverable = false, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'bg-raios-card rounded-lg shadow-card',
          hoverable &&
            'hover:shadow-card-hover hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200',
          className
        )}
        {...props}
      />
    );
  }
);
Card.displayName = 'Card';

/* ─── CardHeader ───────────────────────────────────────────────────────────── */

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn('flex flex-col space-y-1.5 p-6 pb-0', className)}
      {...props}
    />
  );
});
CardHeader.displayName = 'CardHeader';

/* ─── CardTitle ────────────────────────────────────────────────────────────── */

const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => {
  return (
    <h3
      ref={ref}
      className={cn(
        'text-lg font-semibold leading-snug tracking-tight text-raios-text text-balance',
        className
      )}
      {...props}
    />
  );
});
CardTitle.displayName = 'CardTitle';

/* ─── CardDescription ──────────────────────────────────────────────────────── */

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  return (
    <p
      ref={ref}
      className={cn('text-sm text-raios-text-secondary text-pretty', className)}
      {...props}
    />
  );
});
CardDescription.displayName = 'CardDescription';

/* ─── CardContent ──────────────────────────────────────────────────────────── */

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div ref={ref} className={cn('p-6', className)} {...props} />
  );
});
CardContent.displayName = 'CardContent';

/* ─── CardFooter ───────────────────────────────────────────────────────────── */

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn('flex items-center p-6 pt-0', className)}
      {...props}
    />
  );
});
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };
export type { CardProps };
